import React, { useState, type KeyboardEvent } from "react";
import { EyeIcon, EyeOffIcon, Spinner } from "./Shared/Icons";
import type { useLogin } from "../hooks/useLogin";

interface LoginViewProps {
  auth: ReturnType<typeof useLogin>;
  isDark: boolean;
  onShowRegister: () => void;
  onShowRecover: () => void;
}

export const LoginView: React.FC<LoginViewProps> = ({
  auth,
  isDark,
  onShowRegister,
  onShowRecover,
}) => {
  const {
    username,
    password,
    error,
    isLoading,
    setUsername,
    setPassword,
    handleLogin,
    handleSocialLogin,
  } = auth;

  const [showPass, setShowPass] = useState(false);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isLoading) {
      handleLogin();
    }
  };

  const inputClass =
    isDark
      ? "w-full py-4 px-5 border border-white/10 bg-white/[0.03] text-white placeholder-white/20 rounded-xl text-xs font-medium outline-none transition-all focus:border-[#058b95] focus:bg-[#121214]"
      : "w-full py-4 px-5 border border-transparent bg-[#F5F7F4] text-[#212121] placeholder-gray-400 rounded-xl text-xs font-medium outline-none transition-all focus:border-[#058b95] focus:bg-white";

  return (
    <div className="flex flex-col gap-4">
      {/* Email input */}
      <input
        type="text"
        placeholder="Usuario o Email"
        autoComplete="username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={isLoading}
        className={inputClass}
        autoFocus
      />

      {/* Password input */}
      <div className="relative">
        <input
          type={showPass ? "text" : "password"}
          placeholder="Contraseña"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={isLoading}
          className={inputClass}
        />
        <button
          type="button"
          onClick={() => setShowPass(!showPass)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#058b95] transition-all text-xs flex items-center justify-center"
        >
          {showPass ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>

      <button
        onClick={onShowRecover}
        className={`text-xs font-bold text-left self-start mt-1 cursor-pointer transition-all ${
          isDark ? "text-white/60 hover:text-white" : "text-gray-600 hover:text-[#058b95]"
        }`}
      >
        ¿Olvidaste tu contraseña?
      </button>

      <button
        onClick={handleLogin}
        disabled={isLoading}
        className="w-full py-4 bg-[#058b95] hover:bg-[#04727a] disabled:opacity-60 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-cyan-500/20 active:scale-[0.98] mt-4 flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <>
            <Spinner />
            <span>Ingresando...</span>
          </>
        ) : (
          "Ingresar"
        )}
      </button>

      <div className="flex items-center my-1 text-xs text-gray-400">
        <div className="flex-grow border-t border-gray-300 dark:border-white/10"></div>
        <span className="px-3 text-[10px] font-bold uppercase tracking-wider text-gray-400/80">o continuar con</span>
        <div className="flex-grow border-t border-gray-300 dark:border-white/10"></div>
      </div>

      <button
        type="button"
        onClick={() => handleSocialLogin("Google")}
        disabled={isLoading}
        className={`w-full py-4 rounded-xl text-sm font-semibold transition-all active:scale-[0.98] flex items-center justify-center gap-2.5 border ${
          isDark
            ? "bg-white/[0.03] text-white border-white/10 hover:bg-white/[0.08]"
            : "bg-white text-[#212121] border-gray-200 hover:bg-gray-50 shadow-sm"
        }`}
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22c-.22-.66-.35-1.36-.35-2.09z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        <span>Google</span>
      </button>

      <button
        type="button"
        onClick={onShowRegister}
        className={`text-xs font-bold text-center mt-3 cursor-pointer transition-all ${
          isDark ? "text-white/60 hover:text-white" : "text-gray-600 hover:text-[#058b95]"
        }`}
      >
        ¿No tenés cuenta? Registrate
      </button>
    </div>
  );
};
